'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '../lib/supabase/client';

// Get the singleton client once at module level — never recreate it
const supabase = createClient();

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const mountedRef = useRef(true);
  const signingOutRef = useRef(false);

  const clearAuthState = () => {
    setSession(null);
    setUser(null);
    setSubscription(null);
  };

  const handleInvalidSession = () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    clearAuthState();
    // scope:'local' clears storage without a network call — avoids rate limits
    supabase.auth.signOut({ scope: 'local' }).catch(() => {}).finally(() => {
      signingOutRef.current = false;
    });
  };

  const isAuthError = (error: any): boolean => {
    if (!error) return false;
    const authErrorCodes = [
      'refresh_token_not_found',
      'refresh_token_already_used',
      'invalid_refresh_token',
      'session_not_found',
      'user_not_found',
    ];
    return (
      error.__isAuthError === true ||
      error.status === 400 ||
      error.status === 401 ||
      authErrorCodes.includes(error.code)
    );
  };

  useEffect(() => {
    mountedRef.current = true;

    // Initial session check — if the refresh token is stale, this will throw/error
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mountedRef.current) return;
      if (error || !session) {
        if (error && isAuthError(error)) {
          // Stale/invalid refresh token — clear everything
          handleInvalidSession();
        } else if (!session) {
          // No session at all — just clear state, no sign-out needed
          clearAuthState();
        }
      } else {
        setSession(session);
        setUser(session.user ?? null);
        if (session.user) fetchSubscription(session.user.id);
      }
      setLoading(false);
    }).catch(() => {
      if (!mountedRef.current) return;
      handleInvalidSession();
      setLoading(false);
    });

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mountedRef.current) return;

      if (event === 'SIGNED_OUT') {
        clearAuthState();
        setLoading(false);
        return;
      }

      // INITIAL_SESSION fires on mount — if session is null here, tokens are stale
      if (event === 'INITIAL_SESSION' && !session) {
        handleInvalidSession();
        setLoading(false);
        return;
      }

      // TOKEN_REFRESHED with no session means the refresh failed
      if (event === 'TOKEN_REFRESHED' && !session) {
        handleInvalidSession();
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchSubscription(session.user.id);
      else setSubscription(null);
      setLoading(false);
    });

    return () => {
      mountedRef.current = false;
      authSub.unsubscribe();
    };
  }, []);

  const fetchSubscription = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (mountedRef.current) setSubscription(data);
    } catch {
      // ignore
    }
  };

  const isSubscriptionActive = () => {
    if (!subscription) return true;
    if (subscription.status === 'active') return true;
    if (subscription.status === 'trialing') {
      const trialEnd = new Date(subscription.trial_ends_at);
      return trialEnd > new Date();
    }
    return false;
  };

  const signUp = async (email: string, password: string, metadata: { fullName?: string; ranchName?: string } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          ranch_name: metadata?.ranchName || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    clearAuthState();
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    subscription,
    isSubscriptionActive,
    signUp,
    signIn,
    signOut,
    resetPassword,
    getCurrentUser,
    getUserProfile,
    fetchSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
